/**
 * Shared recurrence rule (TMMAPP-0009, MM-D-61): validation and a catalog-template summary.
 * Meetings and plan lines reuse this; they never concatenate raw enum names.
 */
import type { TFunction } from 'i18next';
import { formatCivilDate } from '@/i18n/format';
import type { Frequency, RecurrenceRule, Weekday, WeekOrdinal } from '@/api/planned';

export type RuleError =
  | 'interval'
  | 'weeklyWeekdays'
  | 'monthlyExclusive'
  | 'yearlyExclusive'
  | 'ending'
  | 'endsOn'
  | 'occurrenceCount';

export const FREQUENCIES: readonly Frequency[] = [
  'ONCE',
  'DAILY',
  'WEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'YEARLY',
];

export const WEEKDAYS: readonly Weekday[] = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
export const ORDINALS: readonly WeekOrdinal[] = ['FIRST', 'SECOND', 'THIRD', 'FOURTH', 'LAST'];

const JS_DAY: Record<Weekday, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

export function emptyRule(startOn: string): RecurrenceRule {
  return {
    frequency: 'ONCE',
    interval: 1,
    byWeekdays: null,
    byMonthDays: null,
    byWeekOrdinal: null,
    byOrdinalWeekday: null,
    byMonth: null,
    startOn,
    endsOn: null,
    occurrenceCount: null,
  };
}

/**
 * The rule after the frequency changes: the one transition the recurrence form and the inline
 * recurrence control share, so the same inputs make the same rule (TMMAPP-0103).
 */
export function ruleWithFrequency(prev: RecurrenceRule, frequency: Frequency): RecurrenceRule {
  return {
    ...prev,
    frequency,
    interval: frequency === 'ONCE' ? null : (prev.interval ?? 1),
    byWeekdays: frequency === 'WEEKLY' ? (prev.byWeekdays ?? []) : null,
    byMonthDays:
      frequency === 'MONTHLY' || frequency === 'QUARTERLY' || frequency === 'YEARLY'
        ? (prev.byMonthDays ?? [Number(prev.startOn.slice(8, 10))])
        : null,
    byWeekOrdinal: null,
    byOrdinalWeekday: null,
    byMonth: frequency === 'YEARLY' ? (prev.byMonth ?? Number(prev.startOn.slice(5, 7))) : null,
  };
}

export type DayForm = 'days' | 'ordinal';
export type Ending = 'never' | 'on' | 'after';

export function endingOf(rule: RecurrenceRule): Ending {
  if (rule.endsOn !== null) return 'on';
  if (rule.occurrenceCount !== null) return 'after';
  return 'never';
}

export function formOf(rule: RecurrenceRule): DayForm {
  return rule.byWeekOrdinal !== null ? 'ordinal' : 'days';
}

/** The rule after the ending changes (the form's and the inline control's one transition). */
export function ruleWithEnding(prev: RecurrenceRule, next: Ending): RecurrenceRule {
  if (next === 'never') return { ...prev, endsOn: null, occurrenceCount: null };
  if (next === 'on') return { ...prev, occurrenceCount: null, endsOn: prev.endsOn ?? prev.startOn };
  return { ...prev, endsOn: null, occurrenceCount: prev.occurrenceCount ?? 1 };
}

/** The rule after "on these days" / "on the nth weekday" is picked. */
export function ruleWithDayForm(prev: RecurrenceRule, next: DayForm): RecurrenceRule {
  if (next === 'days') {
    return {
      ...prev,
      byWeekOrdinal: null,
      byOrdinalWeekday: null,
      byMonthDays: prev.byMonthDays ?? [Number(prev.startOn.slice(8, 10))],
    };
  }
  return {
    ...prev,
    byMonthDays: null,
    byWeekOrdinal: prev.byWeekOrdinal ?? 'FIRST',
    byOrdinalWeekday: prev.byOrdinalWeekday ?? 'MON',
  };
}

function hasDays(rule: RecurrenceRule): boolean {
  return rule.byMonthDays !== null && rule.byMonthDays.length > 0;
}

function hasOrdinal(rule: RecurrenceRule): boolean {
  return rule.byWeekOrdinal !== null && rule.byOrdinalWeekday !== null;
}

export function validateRule(rule: RecurrenceRule): RuleError | null {
  if (rule.frequency !== 'ONCE') {
    const interval = rule.interval ?? 1;
    if (!Number.isInteger(interval) || interval < 1) return 'interval';
  }
  if (rule.frequency === 'WEEKLY' && (rule.byWeekdays === null || rule.byWeekdays.length === 0)) {
    return 'weeklyWeekdays';
  }
  if (rule.frequency === 'MONTHLY' || rule.frequency === 'QUARTERLY') {
    if (hasDays(rule) && hasOrdinal(rule)) return 'monthlyExclusive';
    if (!hasDays(rule) && !hasOrdinal(rule)) return 'monthlyExclusive';
  }
  if (rule.frequency === 'YEARLY') {
    if (hasDays(rule) && hasOrdinal(rule)) return 'yearlyExclusive';
    if (!hasDays(rule) && !hasOrdinal(rule)) return 'yearlyExclusive';
  }
  if (rule.endsOn !== null && rule.occurrenceCount !== null) return 'ending';
  if (rule.endsOn !== null && rule.endsOn < rule.startOn) return 'endsOn';
  if (
    rule.occurrenceCount !== null &&
    (!Number.isInteger(rule.occurrenceCount) || rule.occurrenceCount < 1)
  ) {
    return 'occurrenceCount';
  }
  return null;
}

/** Locale-ordered weekdays (Monday-first unless `Intl.Locale.weekInfo` says Sunday). */
export function localeWeekdayOrder(locale: string): Weekday[] {
  let first = 1;
  try {
    const info = (new Intl.Locale(locale) as Intl.Locale & { weekInfo?: { firstDay?: number } })
      .weekInfo;
    if (info?.firstDay === 7) first = 0;
    else if (typeof info?.firstDay === 'number') first = info.firstDay;
  } catch {
    first = 1;
  }
  const byJs = WEEKDAYS.slice().sort((a, b) => JS_DAY[a] - JS_DAY[b]);
  const start = byJs.findIndex((d) => JS_DAY[d] === first);
  if (start <= 0) return byJs;
  return [...byJs.slice(start), ...byJs.slice(0, start)];
}

export function weekdayName(
  day: Weekday,
  locale: string,
  width: 'narrow' | 'short' | 'long',
): string {
  const sunday = new Date(2024, 0, 7);
  const date = new Date(sunday);
  date.setDate(sunday.getDate() + JS_DAY[day]);
  return new Intl.DateTimeFormat(locale, { weekday: width }).format(date);
}

export function monthName(month: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2024, month - 1, 1));
}

function ordinalDay(day: number, locale: string, t: TFunction): string {
  const cat = new Intl.PluralRules(locale, { type: 'ordinal' }).select(day);
  const suffix = t(`recurrence.ordinalSuffix.${cat}`);
  return t('recurrence.dayOrdinal', { day, suffix });
}

function listJoin(items: string[], locale: string): string {
  return new Intl.ListFormat(locale, { style: 'short', type: 'conjunction' }).format(items);
}

function everyPhrase(rule: RecurrenceRule, t: TFunction): string {
  const n = rule.interval ?? 1;
  const unit =
    rule.frequency === 'DAILY'
      ? 'daily'
      : rule.frequency === 'WEEKLY'
        ? 'weekly'
        : rule.frequency === 'MONTHLY'
          ? 'monthly'
          : rule.frequency === 'QUARTERLY'
            ? 'quarterly'
            : 'yearly';
  return t(`recurrence.every.${unit}`, { count: n });
}

function fromUntilTimes(
  rule: RecurrenceRule,
  locale: string,
  t: TFunction,
): {
  from: string;
  until: string;
  times: string;
} {
  const from = t('recurrence.from', { date: formatCivilDate(rule.startOn, locale) });
  let until = '';
  if (rule.endsOn !== null) {
    until = t('recurrence.until', { date: formatCivilDate(rule.endsOn, locale) });
  }
  let times = '';
  if (rule.occurrenceCount !== null) {
    times = t('recurrence.times', { count: rule.occurrenceCount });
  }
  return { from, until, times };
}

/**
 * A human-readable summary composed from catalog templates with named placeholders.
 * Never concatenates raw enum names.
 */
export function describeRecurrence(rule: RecurrenceRule, locale: string, t: TFunction): string {
  const { from, until, times } = fromUntilTimes(rule, locale, t);
  if (rule.frequency === 'ONCE') {
    return t('recurrence.template.once', { from, until, times }).replace(/\s+/g, ' ').trim();
  }
  const every = everyPhrase(rule, t);
  if (rule.frequency === 'DAILY') {
    return t('recurrence.template.daily', { every, from, until, times })
      .replace(/\s+/g, ' ')
      .trim();
  }
  if (rule.frequency === 'WEEKLY') {
    const weekdays = listJoin(
      (rule.byWeekdays ?? []).map((d) => weekdayName(d, locale, 'short')),
      locale,
    );
    return t('recurrence.template.weekly', { every, weekdays, from, until, times })
      .replace(/\s+/g, ' ')
      .trim();
  }
  const days = listJoin(
    (rule.byMonthDays ?? []).map((d) => ordinalDay(d, locale, t)),
    locale,
  );
  const ordinal = rule.byWeekOrdinal !== null ? t(`recurrence.ordinal.${rule.byWeekOrdinal}`) : '';
  const weekday =
    rule.byOrdinalWeekday !== null ? weekdayName(rule.byOrdinalWeekday, locale, 'long') : '';
  const month = rule.byMonth !== null ? monthName(rule.byMonth, locale) : '';
  const form = hasOrdinal(rule) ? 'Ordinal' : 'Days';
  if (rule.frequency === 'MONTHLY' || rule.frequency === 'QUARTERLY') {
    const key =
      rule.frequency === 'MONTHLY'
        ? `recurrence.template.monthly${form}`
        : `recurrence.template.quarterly${form}`;
    return t(key, { every, days, ordinal, weekday, from, until, times })
      .replace(/\s+/g, ' ')
      .trim();
  }
  return t(`recurrence.template.yearly${form}`, {
    every,
    days,
    ordinal,
    weekday,
    month,
    from,
    until,
    times,
  })
    .replace(/\s+/g, ' ')
    .trim();
}

const JS_WEEKDAY_ORDER: readonly Weekday[] = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/** The weekday of a civil date, read from its parts (never `new Date('YYYY-MM-DD')`). */
export function weekdayOfCivil(civil: string): Weekday {
  const day = new Date(
    Number(civil.slice(0, 4)),
    Number(civil.slice(5, 7)) - 1,
    Number(civil.slice(8, 10)),
  ).getDay();
  return JS_WEEKDAY_ORDER[day] ?? 'MON';
}

/** "The nth weekday" a civil date is: first to fourth, or last when it is the month's final one. */
export function ordinalOfCivil(civil: string): 'FIRST' | 'SECOND' | 'THIRD' | 'FOURTH' | 'LAST' {
  const day = Number(civil.slice(8, 10));
  // The fifth of a weekday in a month is the last; the fourth stays "fourth" (it is the last only
  // in a four-weekday month, which a date alone can't tell from a five-weekday one).
  const nth = Math.ceil(day / 7);
  return nth >= 5 ? 'LAST' : (['FIRST', 'SECOND', 'THIRD', 'FOURTH'] as const)[nth - 1]!;
}

/** What a start date sets when recurrence is switched on: weekly, on its weekday. */
export function recurringDefault(startOn: string): RecurrenceRule {
  return {
    ...ruleWithFrequency(emptyRule(startOn), 'WEEKLY'),
    byWeekdays: [weekdayOfCivil(startOn)],
  };
}
